import { useState, useEffect, useCallback, useMemo } from 'react';
import styles from './Formulario.module.css';
import Swal from 'sweetalert2';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css'; 
import {useAuth} from '../../Auth/AuthContext'; 
const AniadirPlantel = ({ abrirModal, cerrarModal, onPlantelCreado }) => {
    const [formData, setFormData] = useState({
        universidad: '',
        direccion: '',
        mapEmbed: '',
    });

    const {getToken} = useAuth(); 
    const token = getToken(); 

    const [universidades, setUniversidades] = useState([]);
    const [cargandoUniversidades, setCargandoUniversidades] = useState(false);
    const [errorUniversidades, setErrorUniversidades] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const cargarUniversidades = async () => {
            setCargandoUniversidades(true);
            setErrorUniversidades('');
            
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/universidades/obtener-universidades`);

                if (!response.ok) {
                    throw new Error(`Error ${response.status}: ${response.statusText}`);
                }
                
                const data = await response.json();
                
                if (Array.isArray(data)) {
                    setUniversidades(data);
                } else if (data && Array.isArray(data.universidades)) {
                    setUniversidades(data.universidades);
                } else {
                    console.error('Formato de datos inesperado:', data);
                    setErrorUniversidades('Formato de datos inesperado del servidor');
                    setUniversidades([]);
                }
            } catch (error) {
                console.error('Error al cargar universidades:', error);
                setErrorUniversidades('Error al cargar las universidades');
                setUniversidades([]);
            } finally {
                setCargandoUniversidades(false);
            }
        };

        if (abrirModal) {
            cargarUniversidades();
        }
    }, [abrirModal]);

    const [telefonos, setTelefonos] = useState(['']);
    const [correos, setCorreos] = useState(['']);
    const [isClosing, setIsClosing] = useState(false);
    
    const [errores, setErrores] = useState({
        universidad: '',
        direccion: '',
        mapEmbed: '',
        telefonos: [''],
        correos: [''],
    });

    const validators = useMemo(() => ({
        email: (email) => {
            if (!email) return '';
            const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
            return !emailRegex.test(email) ? 'El correo no es válido.' : '';
        },
        
        mapEmbed: (embedUrl) => {
            if (!embedUrl) return '';
            return (!embedUrl.includes('google.com/maps/embed') && !embedUrl.includes('maps.google.com/maps'))
                ? 'Debe ser una URL de Google Maps embed válida.'
                : '';
        },
        
        telefono: (telefono) => {
            if (!telefono) return '';
            return (telefono.length < 10 || telefono.length > 15)
                ? 'El número de teléfono debe tener entre 10 y 15 dígitos.'
                : '';
        },
        
        // Nueva validación para dirección
        direccion: (direccion) => {
            if (!direccion || !direccion.trim()) {
                return 'La dirección del plantel es obligatoria.';
            }
            
            const direccionLimpia = direccion.trim();
            
            // Validar longitud mínima
            if (direccionLimpia.length < 10) {
                return 'La dirección debe tener al menos 10 caracteres.';
            }
            
            // Validar longitud máxima
            if (direccionLimpia.length > 200) {
                return 'La dirección no puede exceder 200 caracteres.';
            }
            
            // Validar que contenga al menos una letra y un número (formato típico de dirección)
            const tieneLetras = /[a-zA-ZáéíóúÁÉÍÓÚñÑ]/.test(direccionLimpia);
            const tieneNumeros = /\d/.test(direccionLimpia);
            
            if (!tieneLetras) {
                return 'La dirección debe contener al menos una letra.';
            }
            
            if (!tieneNumeros) {
                return 'La dirección debe contener al menos un número.';
            }
            
            // Validar que no sea solo espacios, números o caracteres especiales
            const soloEspaciosYEspeciales = /^[\s,.\-#°0-9]+$/.test(direccionLimpia);
            if (soloEspaciosYEspeciales) {
                return 'La dirección debe contener texto descriptivo.';
            }
            
            return '';
        }
    }), []);

    const updateError = useCallback((field, error) => {
        setErrores(prev => ({ ...prev, [field]: error }));
    }, []);

    const updateArrayError = useCallback((field, index, error) => {
        setErrores(prev => {
            const newErrors = [...prev[field]];
            newErrors[index] = error;
            return { ...prev, [field]: newErrors };
        });
    }, []);

    const handleInputChange = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        
        // Validar en tiempo real según el campo
        if (field === 'mapEmbed') {
            updateError('mapEmbed', validators.mapEmbed(value));
        } else if (field === 'direccion') {
            updateError('direccion', validators.direccion(value));
        }
    }, [validators, updateError]);

    const handleMapEmbedChange = useCallback((e) => {
        const value = e.target.value;
        handleInputChange('mapEmbed', value);
    }, [handleInputChange]);

    // Nuevo manejador específico para dirección
    const handleDireccionChange = useCallback((e) => {
        const value = e.target.value;
        handleInputChange('direccion', value);
    }, [handleInputChange]);

    const handleTelefonoChange = useCallback((index, value) => {
        const nuevosTelefonos = [...telefonos];
        nuevosTelefonos[index] = value;
        setTelefonos(nuevosTelefonos);
        updateArrayError('telefonos', index, validators.telefono(value));
    }, [telefonos, updateArrayError, validators.telefono]);

    const agregarTelefono = useCallback(() => {
        setTelefonos([...telefonos, '']);
        updateError('telefonos', [...errores.telefonos, '']);
    }, [telefonos, errores.telefonos, updateError]);

    const eliminarTelefono = useCallback((index) => {
        if (telefonos.length > 1) {
            const nuevosTelefonos = telefonos.filter((_, i) => i !== index);
            const nuevosErroresTelefonos = errores.telefonos.filter((_, i) => i !== index);
            setTelefonos(nuevosTelefonos);
            updateError('telefonos', nuevosErroresTelefonos);
        }
    }, [telefonos, errores.telefonos, updateError]);

    const handleCorreoChange = useCallback((index, valor) => {
        const nuevosCorreos = [...correos];
        nuevosCorreos[index] = valor;
        setCorreos(nuevosCorreos);
        updateArrayError('correos', index, validators.email(valor));
    }, [correos, updateArrayError, validators.email]);

    const agregarCorreo = useCallback(() => {
        setCorreos([...correos, '']);
        updateError('correos', [...errores.correos, '']);
    }, [correos, errores.correos, updateError]);

    const eliminarCorreo = useCallback((index) => {
        if (correos.length > 1) {
            const nuevosCorreos = correos.filter((_, i) => i !== index);
            const nuevosErroresCorreos = errores.correos.filter((_, i) => i !== index);
            setCorreos(nuevosCorreos);
            updateError('correos', nuevosErroresCorreos);
        }
    }, [correos, errores.correos, updateError]);

    const handleOverlayClick = useCallback((e) => {
        if (e.target === e.currentTarget) {
            setIsClosing(true);
        }
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsClosing(true);
    }, []);

    // Validación del formulario actualizada
    const validateForm = useCallback(() => {
        const nuevosErrores = {
            universidad: formData.universidad ? '' : 'Debe seleccionar una universidad.',
            direccion: validators.direccion(formData.direccion), // Usar el validador mejorado
            mapEmbed: formData.mapEmbed ? validators.mapEmbed(formData.mapEmbed) : '',
            telefonos: telefonos.map(tel => validators.telefono(tel)),
            correos: correos.map(correo => validators.email(correo)),
        };

        const hayErrores = !!(
            nuevosErrores.universidad ||
            nuevosErrores.direccion ||
            nuevosErrores.mapEmbed ||
            nuevosErrores.telefonos.some(error => error !== '') ||
            nuevosErrores.correos.some(error => error !== '')
        );

        setErrores(nuevosErrores);
        return !hayErrores;
    }, [formData, telefonos, correos, validators]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setLoading(true);

        const telefonosFiltrados = telefonos
            .filter(tel => tel.trim() !== '')
            .map(tel => tel.replace(/\D/g, ''));
        const correosFiltrados = correos.filter(correo => correo.trim() !== '');

        const plantelData = {
            un_clave: parseInt(formData.universidad),
            pl_direccion: formData.direccion.trim(),
            telefonos: telefonosFiltrados,
            correos: correosFiltrados,
            pl_locacion: formData.mapEmbed.trim() || null,
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/crear-plantel`, {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(plantelData),
        })
            if (!response.ok) {
                const data = await response.json(); 
                    throw new Error(data.error || 'Error al crear plantel');
            }

            await response.json(); 

            Swal.fire({
                title: 'Plantel creado correctamente.',
                icon: 'success',
                confirmButtonText: 'Continuar',
                confirmButtonColor: '#242b57',
                customClass: { popup: 'popupbonito' },
            });

            if (onPlantelCreado) {
                await onPlantelCreado(); 
            }

                setIsClosing(true);
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: error.message || 'Ha ocurrido un error al crear el plantel.',
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57',
                customClass: { popup: 'popupbonito' },
            });
        } finally {
            setLoading(false);
        }; 
    }, [formData, correos, telefonos, validateForm]);

    useEffect(() => {
        if (isClosing) {
            const timer = setTimeout(() => {
                cerrarModal();
                setIsClosing(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isClosing, cerrarModal]);

    if (!abrirModal && !isClosing) return null;

    return (
        <div className={styles.modalAniadir} onClick={handleOverlayClick}>
            <div
                className={`${styles.modalAniadirCont} ${
                    abrirModal && !isClosing ? styles.modalAniadirAnim : isClosing ? styles.modalFadeOut : ''
                }`}
            >
                <div className={styles.modalScrollableAniadir}>
                    <button
                        onClick={handleCloseModal}
                        type="button"
                        className={styles.closeBtnAniadir}
                        disabled={loading}
                    >
                        ×
                    </button>
                    <h2 className={styles.tituloAniadir}>Añadir Plantel</h2>

                    {loading && (
                        <div className={styles.loading}>
                            <p>Cargando...</p>
                        </div>
                    )}

                    <form className={styles.formUsuario} onSubmit={handleSubmit}>
                        <label htmlFor="universidad">
                            Universidad:
                            <select
                                id="universidad"
                                value={formData.universidad}
                                onChange={(e) => handleInputChange('universidad', e.target.value)}
                                required
                                disabled={loading}
                            >
                                <option value="">Selecciona una universidad</option>
                                {universidades.map((universidad) => (
                                    <option key={universidad.id} value={universidad.id}>
                                        {universidad.name}
                                    </option>
                                ))}
                            </select>
                            {errores.universidad && <p className={styles.mensajeErrorAniadir}>{errores.universidad}</p>}
                        </label>

                        <label htmlFor="direccion">
                            Dirección del plantel:
                            <small className={styles.helpText}>
                                Incluye calle, número, colonia y ciudad. Ejemplo: "Av. Universidad 123, Col. Centro, Guadalajara"
                            </small>
                            <input
                                id="direccion"
                                value={formData.direccion}
                                placeholder="Ej: Av. Universidad 123, Col. Centro, Guadalajara, Jalisco"
                                onChange={handleDireccionChange}
                                required
                                disabled={loading}
                                minLength="10"
                                maxLength="200"
                            />
                            {errores.direccion && <p className={styles.mensajeErrorAniadir}>{errores.direccion}</p>}
                        </label>

                        <div className={styles.seccionTelefonos}>
                            <label>Teléfonos de contacto (opcional):</label>
                            {telefonos.map((telefono, index) => (
                                <div key={index}>
                                    <div className={styles.inputGroup}>
                                    <div className={styles.inputWrapper}>
                                        <PhoneInput
                                            country={'mx'}
                                            value={telefono}
                                            onChange={(value) => handleTelefonoChange(index, value)}
                                            disabled={loading}
                                            inputClass={styles.inputTelefono}
                                            containerClass={styles.phoneInputContainer}
                                            placeholder="Ingresa un teléfono de contacto"
                                            preferredCountries={['mx', 'us', 'ca']}
                                            enableSearch={true}
                                            searchPlaceholder="Buscar país..."
                                            inputProps={{
                                                'aria-label': `Teléfono de contacto ${index + 1}`,
                                            }}
                                        />
                                        {telefonos.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => eliminarTelefono(index)}
                                                className={styles.botonEliminar}
                                                disabled={loading}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                    </div>
                                    {errores.telefonos[index] && (
                                        <p className={styles.mensajeErrorAniadir}>{errores.telefonos[index]}</p>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={agregarTelefono}
                                className={styles.botonAgregar}
                                disabled={loading}
                            >
                                + Agregar teléfono
                            </button>
                        </div>

                        <div className={styles.seccionCorreos}>
                            <label>Correos electrónicos (opcional):</label>
                            {correos.map((correo, index) => (
                                <div key={index}> 
                                    <div className={styles.inputGroup}>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type="email"
                                            value={correo}
                                            placeholder="Ej: contacto@universidad.edu.mx"
                                            onChange={(e) => handleCorreoChange(index, e.target.value)}
                                            disabled={loading}
                                        />
                                        {correos.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => eliminarCorreo(index)}
                                                className={styles.botonEliminar}
                                                disabled={loading}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                    </div>
                                    {errores.correos[index] && (
                                        <p className={styles.mensajeErrorAniadir}>{errores.correos[index]}</p>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={agregarCorreo}
                                className={styles.botonAgregar}
                                disabled={loading}
                            >
                                + Agregar correo
                            </button>
                        </div>

                        <label htmlFor="mapEmbed">
                            URL de Google Maps Embed (opcional):
                            <small className={styles.helpText}>
                                Para obtener la URL: Ve a Google Maps → Busca la ubicación → Compartir → Insertar un mapa
                                → Copia la URL del src
                            </small>
                            <input
                                id="mapEmbed"
                                value={formData.mapEmbed}
                                placeholder="Ej: https://www.google.com/maps/embed?pb=..."
                                onChange={handleMapEmbedChange}
                                disabled={loading}
                            />            
                        </label>
                        {errores.mapEmbed && <p className={styles.mensajeErrorAniadir}>{errores.mapEmbed}</p>}

                        <button type="submit" className={styles.botonGuard} disabled={loading}>
                            {loading ? 'Guardando...' : 'Guardar Plantel'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AniadirPlantel;